import React from 'react';
import * as api from '../../../services/api';
import ReviewMRSPage from '../../presenters/ManagedRestroomServices/ReviewMRSPage/ReviewMRSPage';
import MRSOrderPage from '../../presenters/ManagedRestroomServices/MRSOrderPage/MRSOrderPage';
import EditItemPage from '../../presenters/ManagedRestroomServices/EditItemPage/EditItemPage';
import { getFullItemName, getProductCodeName, isItemDispenser, priceForItemFrequency, priceForItemPrice } from '../../../utils/mrs-util';
import LoadingWidget from '../../widgets/LoadingWidget/LoadingWidget';
import OverlayLoading from '../../widgets/LoadingWidget/OverlayLoadingWidget';
import { MRS_CUSTOMERS_URL, MRS_THANKYOU } from '../../../route-names';
import { getUserField } from '../../../utils/session-util';
import { add } from 'lodash';

// Used to determine if we want to reflect line item updates before we get a response from the API
// On results in a much faster/smoother user experience, off guarantees accuracy
const optimisticUpdates = true;

class MRSOrderBuilder extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedCategoryIndex: 0, // Used for paging through the categories
      customer: null,
      mrsOrder: {},
      mrsCategories: [], // Contains item categories with nested items and dispensers
      loading: true,
      mrsItems: [], // Only used to get review page data
      lineItemToEdit: null,
      orderUpdateInProgress: false
    };
  }

  componentDidMount() {
    this.getMRSOrder(this.props.match.params.orderId).then(() => {
      api.mrsItems.getDispenserLookups().then((response) => {
        const lookups = response.data;

        api.mrsItems.get().then((response) => {
          const mrsCategories = this.sortMRSItems(response.data, lookups);
          this.setState({ mrsCategories, mrsItems: response.data });
        });
      });
    });
  }

  getMRSOrder = (orderId) => {
    return api.mrsOrders.getById(orderId).then((response) => {
      const order = response.data;
      this.getCustomer(order.branchId, order.routeNumber, order.customerId);
      this.setState({ mrsOrder: order });
    });
  };

  getCustomer = (branchId, routeNumber, customerId) => {
    this.setState({ loading: true });

    api.customers.getOne(branchId, routeNumber, customerId).then((response) => {
      this.setState({ customer: response.data, loading: false });
    });
  };

  sortMRSItems = (items, lookups) => {
    // Split items into groups based on category since that is how they are paged through
    const categorized = items.reduce((acc, item) => {
      const category = acc.find((i) => i.name == item.subCategory);
      // If we haven't came across an item with this category, add a new top element
      if (!category) {
        acc.push({
          name: item.subCategory,
          subCategorySort: item.subCategorySort,
          items: [item]
        });
      } else {
        // If we already have a top level element for this category, just add it to its items
        category.items.push(item);
      }

      return acc;
    }, []);

    const itemSortMap = {
      OW: 1,
      CE: 2
    };

    categorized.forEach((cat) => {
      // Sort each array of elements by fill/dispenser - allowing our dispenser grouping logic to operate more efficiently
      // Then convert the flat array of items into groups of fill items with their dispenser options underneath.
      // This allows us to show duplicate dispensers that may belong to multiple fill items more easily
      cat.items = cat.items
        .sort(
          (a, b) => itemSortMap[a.itemCategory] - itemSortMap[b.itemCategory]
        )
        .reduce((acc, item, i, items) => {
          // If there's a lookup where the current item has a fill product, then we don't need to make a group for it
          if (lookups.find((l) => l.ceItemId == item.id)) {
            return acc;
          }

          const dispenserIds = lookups
            .filter((l) => l.owItemId == item.id)
            .map((l) => l.ceItemId);

          const dispensers = items.filter((i) =>
            dispenserIds.find((d) => d == i.id)
          );

          acc[item.id] = {
            item: { ...item, requiresDispenser: dispensers.length > 0 },
            dispensers: dispensers.sort((a,b) => a.description.localeCompare(b.description))
          };
          return acc;
        }, {});
    });

    return categorized.sort((a, b) => a.subCategorySort - b.subCategorySort);
  };

  handleNextCategory = () => {
    this.setState((prev) => ({
      selectedCategoryIndex: prev.selectedCategoryIndex + 1
    }));
  };

  handleBackCategory = () => {
    this.setState((prev) => ({
      selectedCategoryIndex: prev.selectedCategoryIndex - 1,
      review: false
    }));
  };

  getCurrentItems = () => {
    return Object.values(
      this.state.mrsCategories[this.state.selectedCategoryIndex]?.items || []
    )
    .filter(i => !i.item.isLegacyItem)
    .reduce((acc, item) => {
      acc[item.item.id] = item;
      return acc;
    }, {}); // TODO: This always filters discontinued items; once web can handle edits, we'll need to change the filter to allow discontinued items with existing line items.
  };

  getAllSelectedItemsWithData = () => {
    return this.state.mrsOrder.lineItems.reduce((acc, lineItem) => {
      // Dispensers can be added multiple times for different fill items.
      // Combine them here.
      const existing = acc.find((e) => e.id == lineItem.itemId);

      if (existing) {
        existing.quantity += lineItem.quantity;
      } else {
        acc.push({
          ...lineItem,
          ...this.state.mrsItems.find((i) => i.id == lineItem.itemId)
        });
      }
      return acc;
    }, []);
  };

  addItem = (group, item) => {
    this.setState({ orderUpdateInProgress: true });

    const newLineItems = [...this.state.mrsOrder.lineItems];

    const _isItemDispenser = isItemDispenser(item);
    const customerFrequency = this.state.customer.mrsCustomer.minFrequency;
    const defaultItemFrequency = Math.max(item.defaultFrequency,customerFrequency);

    if (item.requiresDispenser) {
      // Add first matching dispenser
      const dispenser = group.dispensers[0];


      newLineItems.push({
        mrsOrderId: this.state.mrsOrder.mrsOrderId,
        itemId: dispenser.id,
        fillItemId: group.item.id,
        originalPrice: priceForItemFrequency(dispenser, `F${customerFrequency}`,customerFrequency),
        listPrice: priceForItemFrequency(dispenser, `F${customerFrequency}`, customerFrequency),
        adjustedPrice: priceForItemFrequency(dispenser, `F${customerFrequency}`, customerFrequency),
        quantity: 1,
        frequency: `F${customerFrequency}`,
        modifiedDate: Date.now(),
        modifiedBy: getUserField('employeeId'),
        productCode: getProductCodeName(dispenser)
      });
    }

    newLineItems.push({
      mrsOrderId: this.state.mrsOrder.mrsOrderId,
      itemId: item.id,
      fillItemId: _isItemDispenser ? group.item.id : null,
      originalPrice: priceForItemFrequency(item, `F${defaultItemFrequency}`, customerFrequency),
      listPrice: priceForItemFrequency(item, `F${defaultItemFrequency}`, customerFrequency),
      adjustedPrice: priceForItemFrequency(item, `F${defaultItemFrequency}`, customerFrequency),
      quantity: 1,
      frequency: `F${defaultItemFrequency}`,
      modifiedDate: Date.now(),
      modifiedBy: getUserField('employeeId'),
      productCode: getProductCodeName(item)
    });

    if (optimisticUpdates) {
      this.setState((prev) => ({
        ...prev,
        mrsOrder: {
          ...prev.mrsOrder,
          lineItems: newLineItems
        }
      }));
    }

    api.mrsOrders
      .update(this.state.mrsOrder, newLineItems)
      .then((response) => {
        this.setState({
          mrsOrder: response.data,
          orderUpdateInProgress: false
        });
      })
      .catch(() => {
        this.setState({ orderUpdateInProgress: false });
      });
  };

  removeItem = (lineItemId) => {
    this.setState({ orderUpdateInProgress: true });

    // Grab the item ID so we can auto-remove dispensers
    const itemId = this.state.mrsOrder.lineItems.find(
      (i) => i.mrsOrderLineItemId == lineItemId
    )?.itemId;

    const newLineItems = this.state.mrsOrder.lineItems
      .filter(
        // Filter out the line item
        (i) => i.mrsOrderLineItemId != lineItemId
      )
      .filter(
        // Filter out dipsensers that reference the line item being removed
        (i) => i.fillItemId != itemId
      );

    if (optimisticUpdates) {
      this.setState((prev) => ({
        ...prev,
        mrsOrder: {
          ...prev.mrsOrder,
          lineItems: newLineItems
        }
      }));
    }

    api.mrsOrders
      .update(this.state.mrsOrder, newLineItems)
      .then((response) => {
        this.setState({
          mrsOrder: response.data,
          orderUpdateInProgress: false
        });
      })
      .catch(() => {
        this.setState({ orderUpdateInProgress: false });
      });
  };

  cancel = () => {
    if (confirm('Are you sure you want to cancel?')) {
      this.props.history.push(MRS_CUSTOMERS_URL);
    }
  };

  goToReview = () => {
    // Have to have at least one line item to submit
    if (this.state.mrsOrder.lineItems.length > 0) {
      this.handleNextCategory();
      this.setState({ review: true });
    } else {
      alert('Nothing to review. No items have been added to the order.');
    }
  };

  handleEditClick = (group, item, lineItem) => {
    const customerFrequency = this.state.customer.mrsCustomer?.minFrequency;
    const defaultItemFrequency = Math.max(item.defaultFrequency,customerFrequency);

    if (!lineItem) {
      // If they haven't selected this item yet, create an default lineItem to be added to the order if they submit the edit form
      lineItem = {
        mrsOrderId: this.state.mrsOrder.mrsOrderId,
        itemId: item.id,
        fillItemId: isItemDispenser(item) ? group.item.id : null,
        originalPrice: priceForItemFrequency(item, `F${defaultItemFrequency}`, customerFrequency),
        listPrice: priceForItemFrequency(item, `F${defaultItemFrequency}`, customerFrequency),
        adjustedPrice: priceForItemFrequency(item, `F${defaultItemFrequency}`, customerFrequency),
        quantity: 1,
        frequency: `F${defaultItemFrequency}`,
        modifiedDate: Date.now(),
        modifiedBy: getUserField('employeeId'),
        productCode: getProductCodeName(item)
      };
    }
    this.setState({ lineItemToEdit: { ...lineItem, item } });
  };

  handleLineItemUpdate = (lineItemToEdit, values) => {
    this.setState({ orderUpdateInProgress: true });
    let newLineItems = [...this.state.mrsOrder.lineItems];

    // If there's an id, it's already been saved to the order so update it
    // If no id, it needs to be appended to the order as a new line item
    if (lineItemToEdit.mrsOrderLineItemId) {
      newLineItems = newLineItems.map((li) => {
        if (li.mrsOrderLineItemId == lineItemToEdit.mrsOrderLineItemId) {
          return {
            ...li,
            frequency: values.frequency,
            quantity: values.quantity,
            originalPrice: priceForItemPrice(li, li.originalPrice, values.adjustedPrice),
            listPrice: priceForItemFrequency(lineItemToEdit.item, values.frequency, this.state.customer.mrsCustomer.minFrequency),
            adjustedPrice: priceForItemPrice(li, li.adjustedPrice, values.adjustedPrice)
          };
        }
        return li;
      });
    } else {
      newLineItems.push({
        ...lineItemToEdit,
        frequency: values.frequency,
        quantity: values.quantity,

        originalPrice: priceForItemPrice(lineItemToEdit, lineItemToEdit.originalPrice, values.adjustedPrice),
        listPrice: priceForItemFrequency(lineItemToEdit.item, values.frequency, this.state.customer.mrsCustomer.minFrequency),
        adjustedPrice: priceForItemPrice(lineItemToEdit, lineItemToEdit.adjustedPrice, values.adjustedPrice)
      });
    }

    if (optimisticUpdates) {
      this.setState((prev) => ({
        ...prev,
        lineItemToEdit: null,
        mrsOrder: {
          ...prev.mrsOrder,
          lineItems: newLineItems
        }
      }));
    }

    api.mrsOrders.update(this.state.mrsOrder, newLineItems).then((response) => {
      this.setState({
        mrsOrder: response.data,
        lineItemToEdit: null,
        orderUpdateInProgress: false
      });
    });
  };

  handleFinalSubmit = () => {
    // Just in case. Have to have at least one line item to submit
    // Also protected by blocking navigation to review page
    if (this.state.mrsOrder.lineItems.length > 0) {
      this.setState({ loading: true });
      api.mrsOrders
        .submit(this.state.mrsOrder)
        .then((response) => {
          // MRS Order was successfully sent to ABS
          this.setState({ loading: false });
          this.props.history.push(MRS_THANKYOU);
        })
        .catch((reason) => {
          this.setState({ loading: false });
          // Failed to submit for some reason
          console.error(reason);
        });
    } else {
      alert('Cannot submit MRS order with no items.');
    }
  };

  render() {
    if (this.state.loading) {
      return <LoadingWidget />;
    }
    if (this.state.review) {
      return (
        <ReviewMRSPage
          items={this.getAllSelectedItemsWithData()}
          customer={this.state.customer}
          handleBackCategory={this.handleBackCategory}
          handleSubmit={this.handleFinalSubmit}
        />
      );
    } else if (!!this.state.lineItemToEdit) {
      return (
        <>
          {this.state.orderUpdateInProgress && !optimisticUpdates && (
            <OverlayLoading />
          )}
          <EditItemPage
            lineItem={this.state.lineItemToEdit}
            item={this.state.lineItemToEdit.item}
            customer={this.state.customer}
            handleCancel={() => this.setState({ lineItemToEdit: null })}
            handleSave={(values) =>
              this.handleLineItemUpdate(this.state.lineItemToEdit, values)
            }
          />
        </>
      );
    } else {
      const lastCategory =
        this.state.mrsCategories.length - 1 == this.state.selectedCategoryIndex;
      return (
        <>
          {this.state.orderUpdateInProgress && !optimisticUpdates && (
            <OverlayLoading />
          )}
          <MRSOrderPage
            handleNextCategory={this.handleNextCategory}
            handleBackCategory={this.handleBackCategory}
            handleSubmit={this.goToReview}
            cancel={this.cancel}
            firstCategory={this.state.selectedCategoryIndex == 0}
            lastCategory={lastCategory}
            customer={this.state.customer}
            mrsCategories={this.state.mrsCategories}
            selectedCategoryName={
              this.state.mrsCategories[this.state.selectedCategoryIndex]?.name
            }
            currentItems={this.getCurrentItems()}
            mrsOrder={this.state.mrsOrder}
            addItem={this.addItem}
            removeItem={this.removeItem}
            handleEditClick={this.handleEditClick}
          />
        </>
      );
    }
  }
}

export default MRSOrderBuilder;
